import { useRef, useEffect, useState, KeyboardEvent } from "react";
import { Send } from "lucide-react";
import { useLeoChatStore } from "@/stores/leo-chat.store";
import { LeoMessage } from "./leo-message";
import { LeoFab } from "./leo-fab";

const QUICK_REPLIES = [
  "Qual meu saldo?",
  "Últimas ordens",
  "Como faço um saque?",
];

export function LeoPanel() {
  const { messages, isOpen, isLoading, sendMessage } = useLeoChatStore();
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  // Escape to close
  useEffect(() => {
    const handleEscape = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        useLeoChatStore.getState().closePanel();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || isLoading) return;
    setInput("");
    sendMessage(text);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const showQuickReplies = messages.length <= 1 && !isLoading;

  return (
    <>
      <LeoFab />
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-[9998] w-[400px] max-h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden font-sans">
          {/* Header */}
          <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-purple-600 via-purple-500 to-purple-400 text-white">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">
              L
            </div>
            <div className="flex-1">
              <div className="text-base font-bold tracking-tight">Leo</div>
              <div className="text-[11px] opacity-85">
                Assistente do Portal CaaS
              </div>
              <div className="flex items-center gap-1.5 text-[10px] opacity-70 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                Online
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3 max-h-[340px] scroll-smooth">
            {messages.map((msg) => (
              <LeoMessage key={msg.id} message={msg} />
            ))}
            {isLoading && (
              <div className="flex gap-2 self-start">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-600 to-purple-400 flex-shrink-0 flex items-center justify-center text-xs font-bold text-white">
                  L
                </div>
                <div className="flex gap-1 px-3.5 py-2.5 items-center">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce [animation-delay:0ms]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce [animation-delay:200ms]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce [animation-delay:400ms]" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies */}
          {showQuickReplies && (
            <div className="px-5 pb-3 flex flex-wrap gap-1.5">
              {QUICK_REPLIES.map((text) => (
                <button
                  key={text}
                  onClick={() => sendMessage(text)}
                  className="px-3.5 py-1.5 rounded-full border-[1.5px] border-purple-600 bg-white text-purple-600 text-xs font-medium cursor-pointer transition-all hover:bg-purple-50 hover:-translate-y-px"
                >
                  {text}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="flex items-center gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50/80">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Pergunte sobre seus dados..."
              disabled={isLoading}
              className="flex-1 border-[1.5px] border-gray-200 rounded-3xl px-4 py-2.5 text-[13px] outline-none transition-colors focus:border-purple-400 bg-white disabled:opacity-50 font-sans"
            />
            <button
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-purple-400 border-none cursor-pointer flex items-center justify-center transition-transform hover:scale-105 disabled:opacity-50 disabled:cursor-default disabled:hover:scale-100"
            >
              <Send className="w-4 h-4 text-white" />
            </button>
          </div>

          {/* Footer */}
          <div className="px-5 py-1.5 text-center text-[9px] text-gray-400 border-t border-gray-100 bg-gray-50/80">
            Powered by Leo AI
          </div>
        </div>
      )}
    </>
  );
}
